import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  RadioGroup,
  Radio,
  Stack,
  Divider,
  Input,
  Textarea,
} from '@chakra-ui/react';
import {
  CopyIcon,
  DownloadIcon,
  CheckIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import { FaExpand, FaCompress, FaGitlab, FaFolder } from 'react-icons/fa';
import { Markdown } from '../base/Markdown';
// Future: Milkdown for advanced markdown editing
// import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import { Config } from '../../types/config';
import GitLabService from '../../services/gitlabService';
import { API_BASE_URL } from '../../config/constants';
import { useAuth } from '../../contexts/AuthContext';
import { useAppInfoContext } from '../../contexts/AppInfoContext';
import DocumentThreadService from '../../services/documentThreadService';
import { removeThinkTags, hasThinkTags } from '../../utils/contentFilter';

interface ResponseViewerProps {
  content?: string;
  isStreaming?: boolean;
  error?: string | null;
  config: Config | null;
  onStop?: () => void;
  finalTime?: number | null;
  gitlabSourcePath?: string | null;
  // New props for internal state management
  onContentUpdate?: (content: string, isStreaming: boolean) => void;
  onError?: (error: string) => void;
  onStreamingStart?: () => void;
  onStreamingComplete?: (finalTime: number) => void;
}

export interface ResponseViewerRef {
  appendContent: (chunk: string) => void;
  setContent: (content: string) => void;
  setError: (error: string) => void;
  startStreaming: () => void;
  stopStreaming: () => void;
  reset: () => void;
  getEditedContent: () => string;
}

export const ResponseViewer = forwardRef<ResponseViewerRef, ResponseViewerProps>(({
  content: initialContent = '',
  isStreaming: initialIsStreaming = false,
  error: initialError = null,
  config,
  onStop,
  finalTime: initialFinalTime = null,
  gitlabSourcePath,
  onContentUpdate,
  onError: onErrorCallback,
  onStreamingStart,
  onStreamingComplete,
}, ref) => {
  const { user } = useAuth();
  const { appInfo } = useAppInfoContext();
  const threadService = DocumentThreadService.getInstance();
  
  // Internal state management
  const [content, setContent] = useState(initialContent);
  const [isStreaming, setIsStreaming] = useState(initialIsStreaming);
  const [error, setError] = useState(initialError);
  const [finalTime, setFinalTime] = useState(initialFinalTime);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [previousConfigId, setPreviousConfigId] = useState<string | null>(null);

  // Note: Think tag filtering is done at display time in renderContent useMemo
  // This avoids complex partial tag handling during streaming
  
  // Only update internal state from props if we're not currently streaming and the prop is not empty
  useEffect(() => {
    if (!isStreaming && initialContent && initialContent.trim() !== '' && initialContent !== content) {
      console.log('🔄 ResponseViewer updating content from prop:', initialContent.substring(0, 100));
      // Filter out <think> tags when loading from props
      const filteredContent = removeThinkTags(initialContent);
      setContent(filteredContent);
    }
  }, [initialContent, isStreaming]);
  
  useEffect(() => {
    if (initialIsStreaming !== isStreaming) {
      console.log('🔄 ResponseViewer updating isStreaming from prop:', initialIsStreaming);
      setIsStreaming(initialIsStreaming);
    }
  }, [initialIsStreaming]);
  
  useEffect(() => {
    if (initialError !== error) {
      console.log('🔄 ResponseViewer updating error from prop:', initialError);
      setError(initialError);
    }
  }, [initialError]);
  
  useEffect(() => {
    if (initialFinalTime !== finalTime) {
      console.log('🔄 ResponseViewer updating finalTime from prop:', initialFinalTime);
      setFinalTime(initialFinalTime);
    }
  }, [initialFinalTime]);
  
  // Prevent content from being reset by empty props during streaming
  useEffect(() => {
    if (isStreaming && initialContent === '' && content !== '') {
      console.log('🛡️ ResponseViewer preventing content reset during streaming');
      // Don't update content if we're streaming and receiving empty props
    }
  }, [initialContent, isStreaming, content]);

  // Fallback: Clean up think tags after streaming completes (in case display-time filtering misses anything)
  useEffect(() => {
    // Only run cleanup when streaming stops and we have content with think tags
    if (!isStreaming && content && hasThinkTags(content)) {
      console.log('🧹 Post-render cleanup: removing residual think tags from stored content');
      const cleanedContent = removeThinkTags(content);
      if (cleanedContent !== content) {
        setContent(cleanedContent);
        onContentUpdate?.(cleanedContent, false);
      }
    }
  }, [isStreaming]); // Only trigger when streaming state changes

  // Memory management constants
  const MAX_CONTENT_SIZE = 5 * 1024 * 1024; // 5MB limit
  const CONTENT_TRUNCATE_RATIO = 0.7; // Keep 70% of content when truncating
  
  const [activeTab, setActiveTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyFormat, setCopyFormat] = useState<'markdown' | 'rtf'>(() => {
    // Persist copy format preference in sessionStorage
    const savedFormat = sessionStorage.getItem('copyFormat');
    return (savedFormat === 'rtf' || savedFormat === 'markdown') ? savedFormat : 'markdown';
  });
  const [showCopyFormatMenu, setShowCopyFormatMenu] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gitlabUploading, setGitlabUploading] = useState(false);
  const [gitlabUploadSuccess, setGitlabUploadSuccess] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<'md' | 'docx' | 'pdf' | 'txt'>('md');
  const [suggestedPath, setSuggestedPath] = useState<string>('');
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [availableFolders, setAvailableFolders] = useState<string[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [hoveredFolder, setHoveredFolder] = useState<string | null>(null);
  const { isOpen: isRepoModalOpen, onOpen: onRepoModalOpen, onClose: onRepoModalClose } = useDisclosure();
  const [customFilename, setCustomFilename] = useState<string>('');
  const [showFilenameInput, setShowFilenameInput] = useState(false);
  const [gitlabAvailable, setGitlabAvailable] = useState(false);
  const toast = useToast();

  // Auto-scroll functionality
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const lastScrollTop = useRef(0);
  
  // Scroll state management for fullscreen transitions
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Save scroll position before fullscreen toggle
  useEffect(() => {
    if (contentScrollRef.current) {
      setScrollPosition(contentScrollRef.current.scrollTop);
    }
  }, [isFullscreen]);
  
  // Restore scroll position after fullscreen toggle
  useEffect(() => {
    if (contentScrollRef.current && scrollPosition > 0) {
      const timer = setTimeout(() => {
        if (contentScrollRef.current) {
          contentScrollRef.current.scrollTop = scrollPosition;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFullscreen, scrollPosition]);

  // Auto-cleanup only when switching to a different document
  useEffect(() => {
    if (config?.id) {
      // Check if this is a different document (not just the same document updating)
      if (previousConfigId !== null && previousConfigId !== config.id) {
        // Only reset if not currently streaming to avoid interrupting active operations
        if (!isStreaming) {
          console.log('🧹 Clearing memory - switching from document:', previousConfigId, 'to:', config.id);
          
          // Clear previous document content and state
          setContent('');
          setError(null);
          setFinalTime(null);
          setAvailableFolders([]);
          setSelectedFolder('');
          setGitlabUploadSuccess(false);
          setSuggestedPath('');
          
          // Clear sessionStorage when switching documents
          try {
            sessionStorage.removeItem('lastUploadedFile');
          } catch (e) {
            console.warn('Failed to clear sessionStorage on document switch:', e);
          }
        }
      }
      
      // Update the previous config ID
      setPreviousConfigId(config.id);
    }
  }, [config?.id, isStreaming, previousConfigId]);

  // Check GitLab availability when config changes
  useEffect(() => {
    const checkGitlabAvailability = async () => {
      try {
        // Check if either config has gitlabUrl or default repository is available
        const resolvedUrl = await GitLabService.resolveGitLabUrl(config?.gitlabUrl);
        setGitlabAvailable(!!resolvedUrl);
      } catch (error) {
        console.warn('Failed to check GitLab availability:', error);
        setGitlabAvailable(false);
      }
    };
    
    checkGitlabAvailability();
  }, [config?.gitlabUrl]);


  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    appendContent: (chunk: string) => {
      console.log('🔄 ResponseViewer.appendContent called with chunk:', chunk.substring(0, 100), 'chunk length:', chunk.length);

      // Append content directly - think tag filtering happens at display time in renderContent
      // This avoids issues with partial tags split across chunks
      setContent(prevContent => {
        const newContent = prevContent + chunk;

        // Memory management: truncate if content gets too large
        if (newContent.length > MAX_CONTENT_SIZE) {
          const keepSize = Math.floor(MAX_CONTENT_SIZE * CONTENT_TRUNCATE_RATIO);
          const truncatedContent = '...[Content truncated for memory management - showing most recent content]...\n\n'
            + newContent.slice(-keepSize);
          onContentUpdate?.(truncatedContent, true);
          return truncatedContent;
        }

        onContentUpdate?.(newContent, true);
        return newContent;
      });

      // Auto-scroll to bottom for streaming content
      if (contentScrollRef.current && !userHasScrolled) {
        const scrollElement = contentScrollRef.current;
        const shouldAutoScroll = scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 100;

        if (shouldAutoScroll) {
          requestAnimationFrame(() => {
            if (contentScrollRef.current) {
              contentScrollRef.current.scrollTop = contentScrollRef.current.scrollHeight;
            }
          });
        }
      }
    },
    setContent: (newContent: string) => {
      // Filter out <think> tags when setting content
      const filteredContent = removeThinkTags(newContent);
      setContent(filteredContent);
      setEditorContent(filteredContent);
      setIsStreaming(false);
      onContentUpdate?.(filteredContent, false);
    },
    setError: (errorMessage: string) => {
      setError(errorMessage);
      setIsStreaming(false);
      if (startTime) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setFinalTime(elapsed);
        onStreamingComplete?.(elapsed);
        setStartTime(null);
      }
      onErrorCallback?.(errorMessage);
    },
    startStreaming: () => {
      console.log('🚀 ResponseViewer.startStreaming called');
      // Complete reset to ensure fresh state for new request
      setContent('');
      setEditorContent('');
      setError(null);
      setIsStreaming(true);
      setStartTime(Date.now());
      setFinalTime(null);

      // Reset all UI states to ensure clean start
      setCopySuccess(false);
      setExportSuccess(false);
      setShowDownloadDropdown(false);
      setUserHasScrolled(false);
      setGitlabUploadSuccess(false);
      setCustomFilename('');
      setShowFilenameInput(false);

      // Reset scroll position
      if (contentScrollRef.current) {
        contentScrollRef.current.scrollTop = 0;
      }

      console.log('✅ ResponseViewer streaming started, content reset');
      onStreamingStart?.();
    },
    stopStreaming: () => {
      console.log('⏹ ResponseViewer.stopStreaming called, current content length:', content.length);

      setIsStreaming(false);
      if (startTime) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setFinalTime(elapsed);
        onStreamingComplete?.(elapsed);
        setStartTime(null);
      }
      console.log('✅ ResponseViewer streaming stopped');
    },
    reset: () => {
      console.log('🔄 ResponseViewer.reset called');

      setContent('');
      setEditorContent('');
      setError(null);
      setIsStreaming(false);
      setFinalTime(null);
      setStartTime(null);

      // Clear GitLab-related state to free memory
      setAvailableFolders([]);
      setSelectedFolder('');
      setGitlabUploadSuccess(false);
      setSuggestedPath('');
      setShowFolderBrowser(false);
      setNewFolderName('');
      setShowCreateFolder(false);
      setHoveredFolder(null);

      // Reset editing state
      setIsEditing(false);

      // Reset filename state
      setCustomFilename('');
      setShowFilenameInput(false);

      // Clear sessionStorage for this component
      try {
        sessionStorage.removeItem('lastUploadedFile');
      } catch (e) {
        console.warn('Failed to clear sessionStorage:', e);
      }
      console.log('✅ ResponseViewer reset complete');
    },
    getEditedContent: () => {
      return editorContent;
    },
  }));

  // Editor configuration
  const [editorContent, setEditorContent] = useState(content);
  const [isEditing, setIsEditing] = useState(false);
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  // For editing mode, use the editor content; otherwise use the raw content
  const activeContent = isEditing ? editorContent : content;
  const wordCount = activeContent ? activeContent.split(/\s+/).filter(word => word.length > 0).length : 0;
  const characterCount = activeContent ? activeContent.length : 0;

  // Reset copy/export success states
  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => setCopySuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  useEffect(() => {
    if (exportSuccess) {
      const timer = setTimeout(() => setExportSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [exportSuccess]);

  // Auto-scroll to bottom during streaming unless user has manually scrolled
  useEffect(() => {
    if (isStreaming && !userHasScrolled && contentScrollRef.current) {
      const scrollElement = contentScrollRef.current;
      // Use requestAnimationFrame for smoother scrolling during streaming
      requestAnimationFrame(() => {
        if (scrollElement && isStreaming && !userHasScrolled) {
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      });
    }
  }, [content, isStreaming, userHasScrolled]);

  // Reset user scroll state when streaming starts
  useEffect(() => {
    if (isStreaming) {
      setUserHasScrolled(false);
    }
  }, [isStreaming]);



  // Handle user scroll detection
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollElement = e.currentTarget;
    const currentScrollTop = scrollElement.scrollTop;
    
    // If user scrolled up (away from bottom), mark as manually scrolled
    if (currentScrollTop < lastScrollTop.current) {
      setUserHasScrolled(true);
    }
    
    // If user scrolled to bottom, reset auto-scroll (more generous threshold for normal mode)
    const threshold = isFullscreen ? 10 : 30;
    const isAtBottom = Math.abs(scrollElement.scrollHeight - scrollElement.clientHeight - currentScrollTop) < threshold;
    if (isAtBottom) {
      setUserHasScrolled(false);
    }
    
    lastScrollTop.current = currentScrollTop;
  };

  // Convert markdown to RTF format
  const convertToRTF = (markdown: string): string => {
    // Basic RTF header
    let rtf = '{\\rtf1\\ansi\\deff0 {\\fonttbl{\\f0 Times New Roman;}}';
    rtf += '\\viewkind4\\uc1\\pard\\f0\\fs24 ';
    
    // Convert markdown to RTF
    let lines = markdown.split('\n');
    let inCodeBlock = false;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Handle code blocks
      if (line.startsWith('```')) {
        inCodeBlock = !inCodeBlock;
        continue;
      }
      
      if (inCodeBlock) {
        // Code block formatting
        rtf += '\\f1 ' + line.replace(/[\\{}]/g, '\\$&') + '\\line ';
        continue;
      }
      
      // Handle headers
      if (line.startsWith('# ')) {
        rtf += '\\b\\fs32 ' + line.substring(2).replace(/[\\{}]/g, '\\$&') + '\\b0\\fs24\\par ';
      } else if (line.startsWith('## ')) {
        rtf += '\\b\\fs28 ' + line.substring(3).replace(/[\\{}]/g, '\\$&') + '\\b0\\fs24\\par ';
      } else if (line.startsWith('### ')) {
        rtf += '\\b\\fs26 ' + line.substring(4).replace(/[\\{}]/g, '\\$&') + '\\b0\\fs24\\par ';
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        // Bullet points
        rtf += '\\bullet\\tab ' + line.substring(2).replace(/[\\{}]/g, '\\$&') + '\\par ';
      } else if (line.trim() === '') {
        // Empty line
        rtf += '\\par ';
      } else {
        // Regular text with inline formatting
        let processedLine = line;
        
        // Bold text
        processedLine = processedLine.replace(/\*\*(.*?)\*\*/g, '\\b $1\\b0 ');
        
        // Italic text
        processedLine = processedLine.replace(/\*(.*?)\*/g, '\\i $1\\i0 ');
        
        // Escape special RTF characters
        processedLine = processedLine.replace(/[\\{}]/g, '\\$&');
        
        rtf += processedLine + '\\par ';
      }
    }
    
    rtf += '}';
    return rtf;
  };

  const handleCopy = async () => {
    const contentToCopy = editorContent || content;
    if (!contentToCopy) return;
    
    try {
      if (copyFormat === 'rtf') {
        // Use modern Clipboard API with HTML format for better browser support
        if (navigator.clipboard && navigator.clipboard.write) {
          // Convert markdown to HTML
          let htmlContent = contentToCopy;
          
          // Escape HTML entities first
          htmlContent = htmlContent.replace(/&/g, '&amp;')
                                 .replace(/</g, '&lt;')
                                 .replace(/>/g, '&gt;');
          
          // Convert code blocks first (before other conversions)
          htmlContent = htmlContent.replace(/```([\s\S]*?)```/g, (match, code) => {
            const cleanCode = code.trim();
            return `<pre style="background-color: #f5f5f5; padding: 10px; border-radius: 4px; font-family: monospace;"><code>${cleanCode}</code></pre>`;
          });
          
          // Convert headers
          htmlContent = htmlContent.replace(/^### (.*?)$/gm, '<h3 style="font-size: 1.17em; font-weight: bold; margin: 0.5em 0;">$1</h3>');
          htmlContent = htmlContent.replace(/^## (.*?)$/gm, '<h2 style="font-size: 1.5em; font-weight: bold; margin: 0.5em 0;">$1</h2>');
          htmlContent = htmlContent.replace(/^# (.*?)$/gm, '<h1 style="font-size: 2em; font-weight: bold; margin: 0.5em 0;">$1</h1>');
          
          // Convert bold and italic (handle nested cases)
          htmlContent = htmlContent.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
          htmlContent = htmlContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          htmlContent = htmlContent.replace(/\*(.*?)\*/g, '<em>$1</em>');
          
          // Convert inline code
          htmlContent = htmlContent.replace(/`([^`]+)`/g, '<code style="background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-family: monospace;">$1</code>');
          
          // Convert lists with proper nesting
          const lines = htmlContent.split('\n');
          let inList = false;
          let processedLines = [];
          
          for (let i = 0; i < lines.length; i++) {
            let line = lines[i];
            
            if (line.match(/^[-*] /)) {
              if (!inList) {
                processedLines.push('<ul style="margin: 0.5em 0; padding-left: 2em;">');
                inList = true;
              }
              processedLines.push(`<li>${line.substring(2)}</li>`);
            } else if (line.match(/^\d+\. /)) {
              if (!inList) {
                processedLines.push('<ol style="margin: 0.5em 0; padding-left: 2em;">');
                inList = true;
              }
              processedLines.push(`<li>${line.replace(/^\d+\. /, '')}</li>`);
            } else {
              if (inList && line.trim() === '') {
                processedLines.push(inList.toString().includes('<ul') ? '</ul>' : '</ol>');
                inList = false;
              }
              processedLines.push(line);
            }
          }
          
          if (inList) {
            processedLines.push(inList.toString().includes('<ul') ? '</ul>' : '</ol>');
          }
          
          htmlContent = processedLines.join('\n');
          
          // Convert line breaks to <br> (but not inside pre tags)
          htmlContent = htmlContent.replace(/\n/g, '<br>');
          
          // Wrap in proper HTML structure
          const fullHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; }
pre { overflow-x: auto; }
code { font-family: 'Courier New', Courier, monospace; }
</style>
</head>
<body>${htmlContent}</body>
</html>`;

          // Create blob with both HTML and plain text
          const htmlBlob = new Blob([fullHtml], { type: 'text/html' });
          const textBlob = new Blob([contentToCopy], { type: 'text/plain' });
          
          // Use ClipboardItem to write both formats
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': htmlBlob,
              'text/plain': textBlob
            })
          ]);
          
          setCopySuccess(true);
          setShowCopyFormatMenu(false);
          toast({
            title: 'Copied to clipboard',
            description: 'Content copied as rich text for Word/Excel.',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        } else {
          // Fallback for browsers without clipboard.write support
          // Create a contenteditable div for better compatibility
          const tempDiv = document.createElement('div');
          tempDiv.contentEditable = 'true';
          tempDiv.style.position = 'fixed';
          tempDiv.style.left = '0';
          tempDiv.style.top = '0';
          tempDiv.style.opacity = '0';
          tempDiv.style.pointerEvents = 'none';
          
          // Simple HTML conversion for fallback
          let simpleHtml = contentToCopy;
          simpleHtml = simpleHtml.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
          simpleHtml = simpleHtml.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
          simpleHtml = simpleHtml.replace(/^# (.*?)$/gm, '<h1>$1</h1>');
          simpleHtml = simpleHtml.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
          simpleHtml = simpleHtml.replace(/\*(.*?)\*/g, '<i>$1</i>');
          simpleHtml = simpleHtml.replace(/\n/g, '<br>');
          
          tempDiv.innerHTML = simpleHtml;
          document.body.appendChild(tempDiv);
          
          // Focus and select
          tempDiv.focus();
          document.execCommand('selectAll', false, undefined);
          
          // Copy
          const copySuccess = document.execCommand('copy');
          
          // Clean up
          document.body.removeChild(tempDiv);
          
          if (copySuccess) {
            setCopySuccess(true);
            setShowCopyFormatMenu(false);
            toast({
              title: 'Copied to clipboard',
              description: 'Content copied as rich text.',
              status: 'success',
              duration: 2000,
              isClosable: true,
            });
          } else {
            throw new Error('Copy command failed');
          }
        }
      } else {
        // Copy as plain markdown
        await navigator.clipboard.writeText(contentToCopy);
        setCopySuccess(true);
        setShowCopyFormatMenu(false);
        toast({
          title: 'Copied to clipboard',
          description: 'Content copied as Markdown.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Copy error:', error);
      toast({
        title: 'Copy failed',
        description: 'Failed to copy content to clipboard.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Generate default filename
  const generateDefaultFilename = (format: string) => {
    const documentType = config?.name || 'document';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    }).replace(/\//g, '-');
    const timeStr = now.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    }).replace(/:/g, '-');
    const timestamp = `${dateStr}_${timeStr}`;
    
    const baseNameReadable = (config?.name || 'Document')
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars but keep spaces
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
    
    return `${baseNameReadable} - ${timestamp}.${format}`;
  };

  const handleDownload = async (format: 'md' | 'docx' | 'pdf') => {
    const contentToDownload = editorContent || content;
    if (!contentToDownload) return;
    
    try {
      setExportSuccess(true);
      
      // Use custom filename if provided, otherwise generate default
      const filename = customFilename.trim() || generateDefaultFilename(format);
      
      let blob: Blob;
      
      if (format === 'md') {
        // Handle MD format directly
        blob = new Blob([contentToDownload], { type: 'text/markdown' });
      } else {
        // Use backend conversion API for PDF and DOCX
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/convert/${format}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: contentToDownload
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Conversion failed: ${response.status} - ${errorText}`);
        }
        
        blob = await response.blob();
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: `Your document is being downloaded as ${format.toUpperCase()}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
     
      // Reset custom filename after successful download
      setCustomFilename('');
      setShowFilenameInput(false);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Failed to download the document.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleGitlabUpload = async (format: 'md' | 'docx' | 'pdf' | 'txt') => {
    const contentToUpload = editorContent || content;
    if (!contentToUpload) return;
    
    // Resolve GitLab URL (config-specific or default)
    const resolvedGitlabUrl = await GitLabService.resolveGitLabUrl(config?.gitlabUrl);
    if (!resolvedGitlabUrl) {
      toast({
        title: 'GitLab Repository Not Available',
        description: 'No GitLab repository configured. Please configure a default repository in Settings.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setGitlabUploading(true);
      
      // Create human-readable timestamp and filename
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).replace(/\//g, '-');
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit' 
      }).replace(/:/g, '-');
      const timestamp = `${dateStr}_${timeStr}`;
      
      const documentTypeName = config?.name || 'Document'; // Use the document type name from config
      
      // Create human-readable filename
      const baseNameReadable = (config?.name || 'Document')
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars but keep spaces
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      
      let fileContent = contentToUpload;
      // Use custom filename if provided, otherwise use default
      let fileName = customFilename.trim() || `${baseNameReadable} - ${timestamp}.${format}`;
      
      // Create meaningful commit message with app name and username
      const username = user?.username || 'User';
      let commitMessage = `AI Generated ${config?.name || 'Document'} by ${appInfo.name}-Bot (${username})`;
      
      // Add source file link if available from GitLab upload
      if (gitlabSourcePath && resolvedGitlabUrl) {
        // Extract the source filename from the GitLab path (assuming it was uploaded from GitLab)
        const sourceFileInfo = sessionStorage.getItem('lastUploadedFile');
        if (sourceFileInfo) {
          try {
            const sourceFile = JSON.parse(sourceFileInfo);
            if (sourceFile.gitlabUrl && sourceFile.filePath) {
              // Create GitLab link to source file
              const gitlabBaseUrl = sourceFile.gitlabUrl.replace(/\/-\/.*$/, '');
              const sourceFileLink = `${gitlabBaseUrl}/-/blob/main/${sourceFile.filePath}`;
              commitMessage += `\n\nSource: ${sourceFileLink}`;
            }
          } catch (e) {
            console.warn('Could not parse source file info:', e);
          }
        }
      }
      
      // Handle different file formats
      if (format === 'md') {
        // Content is already in markdown format
        fileContent = contentToUpload;
      } else if (format === 'txt') {
        // Convert markdown to plain text (remove markdown syntax)
        fileContent = contentToUpload.replace(/#{1,6}\s/g, '')
                            .replace(/\*\*(.*?)\*\*/g, '$1')
                            .replace(/\*(.*?)\*/g, '$1')
                            .replace(/\[(.*?)\]\(.*?\)/g, '$1')
                            .replace(/`(.*?)`/g, '$1');
      } else {
        // For DOCX and PDF, convert using backend API and get binary content
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_BASE_URL}/api/convert/${format}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'text/plain',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: contentToUpload
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Conversion failed: ${response.status} - ${errorText}`);
          }
          
          const blob = await response.blob();
          
          // Convert blob to base64 for GitLab upload
          const arrayBuffer = await blob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const binaryString = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('');
          fileContent = btoa(binaryString);
        } catch (conversionError) {
          console.error('Document conversion error:', conversionError);
          throw new Error(`Failed to convert document to ${format.toUpperCase()}: ${conversionError instanceof Error ? conversionError.message : 'Unknown error'}`);
        }
      }
      
      // Use the selected folder for upload
      const folderStructure = selectedFolder || suggestedPath;
      console.log('🗂️ Debug - selected folder:', selectedFolder);
      console.log('🗂️ Debug - suggested path:', suggestedPath);
      console.log('🗂️ Debug - final folderStructure:', folderStructure);
      
      const result = await GitLabService.uploadFile(
        resolvedGitlabUrl,
        fileName,
        fileContent,
        commitMessage,
        folderStructure
      );
      
      setGitlabUploadSuccess(true);
      
      // Show the actual save path in the success message
      const finalPath = `${folderStructure}/${fileName}`;
      const gitlabUrl = result.url; // Full GitLab URL from backend response
      
      // Show success toast with copy functionality
      toast({
        render: () => (
          <Box
            p={3}
            bg="green.500"
            color="white"
            borderRadius="md"
            cursor="pointer"
            onClick={() => {
              navigator.clipboard.writeText(gitlabUrl).then(() => {
                toast({
                  title: 'URL copied',
                  description: 'Full GitLab URL copied to clipboard',
                  status: 'success',
                  duration: 2000,
                  isClosable: true,
                });
              }).catch(() => {
                toast({
                  title: 'Copy failed',
                  description: 'Failed to copy URL to clipboard',
                  status: 'error',
                  duration: 2000,
                  isClosable: true,
                });
              });
            }}
          >
            Document saved to {finalPath}. Click to copy GitLab URL.
          </Box>
        ),
        position: 'top-right',
        duration: 8000,
        isClosable: true,
        status: 'success',
      });
      
      console.log('✅ GitLab upload successful:', result);
     
      // Reset custom filename after successful upload
      setCustomFilename('');
    } catch (error) {
      console.error('GitLab upload error:', error);
      toast({
        title: 'Repository Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save to GitLab repository',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGitlabUploading(false);
    }
  };

  useEffect(() => {
    if (gitlabUploadSuccess) {
      const timer = setTimeout(() => setGitlabUploadSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [gitlabUploadSuccess]);

  // Custom close handler for GitLab modal
  const handleRepoModalClose = () => {
    setCustomFilename('');
    onRepoModalClose();
  };

  // Calculate suggested path when modal opens
  useEffect(() => {
    if (isRepoModalOpen && config) {
      calculateSuggestedPath();
    }
  }, [isRepoModalOpen, config, gitlabSourcePath]);

  const calculateSuggestedPath = () => {
    if (!config?.gitlabUrl) return;

    const documentTypeName = config.name 
      ? config.name.replace(/[^a-zA-Z0-9\s]/g, '').trim()
      : 'document';

    // Get source file information
    let sourcePath = gitlabSourcePath;
    let sourceFileName = 'generated';
    
    if (!sourcePath) {
      const sourceFileInfo = sessionStorage.getItem('lastUploadedFile');
      if (sourceFileInfo) {
        try {
          const sourceFile = JSON.parse(sourceFileInfo);
          if (sourceFile.filePath) {
            // Extract directory path from the source file path
            sourcePath = sourceFile.filePath.includes('/') 
              ? sourceFile.filePath.substring(0, sourceFile.filePath.lastIndexOf('/'))
              : '';
            
            // Extract filename without extension
            const fullPath = sourceFile.filePath;
            const fileName = fullPath.includes('/') 
              ? fullPath.substring(fullPath.lastIndexOf('/') + 1)
              : fullPath;
            sourceFileName = fileName.includes('.') 
              ? fileName.substring(0, fileName.lastIndexOf('.'))
              : fileName;
          }
        } catch (e) {
          console.warn('Could not parse source file info for path:', e);
        }
      }
    }
    
    // If sourcePath contains a file extension, extract the directory path
    if (sourcePath && sourcePath.includes('.')) {
      sourcePath = sourcePath.includes('/') 
        ? sourcePath.substring(0, sourcePath.lastIndexOf('/'))
        : '';
    }

    // Determine suggested path based on scenarios
    let suggestedPath: string;
    
    if (sourcePath) {
      // Scenario 1 & 2: Input file provided
      if (sourcePath.includes('AI-Drafts')) {
        // Scenario 2: Already inside AI-Drafts - find the appropriate level
        const pathParts = sourcePath.split('/');
        const aiDraftsIndex = pathParts.findIndex(part => part === 'AI-Drafts');
        
        if (aiDraftsIndex !== -1) {
          // When already inside AI-Drafts, go back to the first AI-Drafts folder
          // and create the new document type folder there
          const basePath = pathParts.slice(0, aiDraftsIndex + 1).join('/');
          suggestedPath = `${basePath}/${documentTypeName}`;
        } else {
          // Fallback: just append document type to source path
          suggestedPath = `${sourcePath}/${documentTypeName}`;
        }
      } else {
        // Scenario 1: Not inside AI-Drafts
        suggestedPath = `${sourcePath}/AI-Drafts/${documentTypeName}`;
      }
    } else {
      // Scenario 3: No input file (Direct Generation)
      suggestedPath = `AI-Drafts/${documentTypeName}`;
    }

    setSuggestedPath(suggestedPath);
    setSelectedFolder(suggestedPath); // Set as default selected folder
  };

  const loadAvailableFolders = async () => {
    if (!config?.gitlabUrl) return;
    
    setLoadingFolders(true);
    try {
      const files = await GitLabService.getFiles(config.gitlabUrl, '', '');
      const folders = files
        .filter(file => file.type === 'tree')
        .map(file => file.path)
        .slice(0, 100); // Limit to 100 folders to prevent memory issues
      
      // Add root level
      folders.unshift('');
      setAvailableFolders(folders);
    } catch (error) {
      console.error('Error loading folders:', error);
      toast({
        title: 'Error loading folders',
        description: 'Failed to load repository folders',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingFolders(false);
    }
  };

  const createNewFolder = async (parentPath: string = '') => {
    if (!config?.gitlabUrl || !newFolderName.trim()) return;
    
    setCreatingFolder(true);
    try {
      // Create the folder path by combining parent path with new folder name
      const folderPath = parentPath ? `${parentPath}/${newFolderName.trim()}` : newFolderName.trim();
      const readmeContent = `# ${newFolderName.trim()}\n\nThis folder was created via ${appInfo.name}.`;
      
      const result = await GitLabService.uploadFile(
        config.gitlabUrl,
        'README.md',
        readmeContent,
        `Create ${folderPath} folder - ${appInfo.name}-Bot`,
        folderPath
      );
      
      if (result.success) {
        toast({
          title: 'Folder created',
          description: `Successfully created folder: ${folderPath}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Reset form and reload folders
        setNewFolderName('');
        setShowCreateFolder(false);
        setHoveredFolder(null);
        await loadAvailableFolders();
      } else {
        throw new Error('Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast({
        title: 'Failed to create folder',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleStop = () => {
    onStop?.();
    toast({
      title: 'Stopping generation',
      description: 'The document generation has been stopped.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const toggleFullscreen = () => {
    // Save current scroll position before toggling
    if (contentScrollRef.current) {
      setScrollPosition(contentScrollRef.current.scrollTop);
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleShare = async () => {
    if (!config || !content) return;
    
    try {
      // Check if the current config has sharing enabled
      if (config.isShared && config.shareId) {
        const shareUrl = `${window.location.origin}/shared/${config.shareId}`;
        await navigator.clipboard.writeText(shareUrl);
        
        toast({
          title: 'Share link copied',
          description: 'The share link has been copied to your clipboard.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sharing not available',
          description: 'This document configuration is not shared. Enable sharing in Settings.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Share failed',
        description: 'Failed to copy share link to clipboard.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Update editor content when content changes (but not when editing)
  useEffect(() => {
    if (!isEditing) {
      setEditorContent(content);
    }
  }, [content, isEditing]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Save the edited content when switching from Edit to Preview
      onContentUpdate?.(editorContent, false);
      console.log('💾 Saving edited content:', editorContent.substring(0, 100) + '...');
    }
    setIsEditing(!isEditing);
  };


  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log('📝 handleContentChange triggered:', e.target.value);
    const newContent = e.target.value;
    setEditorContent(newContent);
    onContentUpdate?.(newContent, false);
  };

  // Milkdown content change handler
  const handleMilkdownChange = (markdown: string) => {
    console.log('📝 Milkdown content changed:', markdown.substring(0, 100) + '...');
    setEditorContent(markdown);
    onContentUpdate?.(markdown, false);
  };

  // Debug when editing mode changes
  useEffect(() => {
    console.log('🔄 isEditing changed to:', isEditing);
    if (isEditing) {
      // Check for ResponseViewer textarea specifically after a delay
      setTimeout(() => {
        const textarea = document.getElementById('response-editor-textarea') as HTMLTextAreaElement;
        if (textarea) {
          console.log('✅ ResponseViewer textarea found in DOM:', {
            disabled: textarea.disabled,
            readOnly: textarea.readOnly,
            visible: textarea.offsetHeight > 0 && textarea.offsetWidth > 0,
            value: textarea.value,
            placeholder: textarea.placeholder,
            zIndex: getComputedStyle(textarea).zIndex
          });
          
          // Force focus on the ResponseViewer textarea
          textarea.focus();
          console.log('🎯 Focus attempted on ResponseViewer textarea, activeElement:', document.activeElement?.tagName || 'null');
          console.log('🎯 Active element ID:', (document.activeElement as HTMLElement)?.id || 'none');
        } else {
          console.log('❌ ResponseViewer textarea not found in DOM');
        }
      }, 200);
    }
  }, [isEditing]);

  // Future: Milkdown Editor Component for advanced editing
  // Will be implemented after resolving API compatibility issues

  // Render content with editing capability
  // Filter think tags at display time for clean UI (avoids complex partial tag handling during streaming)
  const renderContent = useMemo(() => {
    if (isEditing) {
      return (
        <Textarea
          value={editorContent}
          onChange={handleContentChange}
          placeholder="Edit your response here..."
          size="md"
          minH="400px"
          resize="vertical"
          bg={cardBg}
          color={textColor}
          _focus={{
            outline: "none",
            boxShadow: "none",
            borderColor: "transparent"
          }}
          _placeholder={{
            color: mutedColor,
            opacity: 0.6
          }}
          autoFocus
          spellCheck={false}
        />
      );
    } else {
      // Filter think tags at display time - this handles all edge cases robustly
      // including tags split across streaming chunks
      const displayContent = removeThinkTags(activeContent);
      return <Markdown content={displayContent} className="response-markdown" />;
    }
  }, [isEditing, editorContent, activeContent, cardBg]);

  if (error) {
    return (
      <Box className="response-viewer error-state" bg={cardBg} h="400px">
        <Center h="full" p={6}>
          <Alert borderRadius="md" flexDirection="column" textAlign="center" bg="red.50" borderColor="red.200">
            <AlertIcon boxSize="40px" mr={0} mb={4} />
            <Text fontWeight="semibold" mb={2}>
              Processing Error
            </Text>
            <Text fontSize="sm" color={mutedColor}>
              {error}
            </Text>
          </Alert>
        </Center>
      </Box>
    );
  }

  if (isStreaming && !content) {
    console.log('🔄 ResponseViewer showing loading state - isStreaming:', isStreaming, 'content length:', content.length);
    return (
      <Box className="response-viewer loading-state" bg={cardBg} h="400px">
        <Center h="full">
          <VStack spacing={4} className="loading-content">
            <Spinner size="xl" color="primary.500" />
            <Text color={mutedColor}>Generating your document...</Text>
            <Text fontSize="sm" color={mutedColor}>
              AI is processing your request and streaming the response
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box className="response-viewer empty-state" bg={cardBg} h="400px">
        <Center h="full">
          <VStack spacing={4} className="empty-state-content" textAlign="center">
            <Box className="empty-state-icon">
              <Text fontSize="3xl">✨</Text>
            </Box>
            <Text className="empty-state-text" color={mutedColor} fontSize="lg">
              Nothing to see here yet. Enter text and click on Execute to see a Response
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box 
         ref={containerRef}
         className={`response-viewer${isFullscreen ? ' fullscreen' : ''}`}
         position={isFullscreen ? 'fixed' : 'relative'} 
         top={isFullscreen ? '0' : 'auto'} 
         left={isFullscreen ? '0' : 'auto'} 
         w={isFullscreen ? '100vw' : 'auto'} 
         h={isFullscreen ? '100vh' : '100%'}
         maxH={isFullscreen ? '100vh' : 'none'} 
         zIndex={isFullscreen ? 9999 : 'auto'} 
         borderRadius={isFullscreen ? '0' : 'md'}
         bg={cardBg}
         display="flex"
         flexDirection="column"
         border="1px"
         borderColor={borderColor}
         overflow="hidden">
      <Box className="viewer-header" 
           p={4} 
           borderBottom="1px" 
           borderColor={borderColor} 
           bg={cardBg}
           position="sticky"
           top="0"
           zIndex={10}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Box className="header-left">
            <HStack spacing={3}>
              <Box className="header-icon"
                   p={2}
                   borderRadius="md"
                   bg={useColorModeValue('blue.50', 'blue.900')}
                   color={useColorModeValue('blue.600', 'blue.200')}>
                <Text fontSize="lg">✨</Text>
              </Box>
              <Text className="header-title" fontWeight="semibold" fontSize="lg" color={textColor}>
                Response
              </Text>
            </HStack>
          </Box>
          
          <Box className="header-actions">
            <HStack spacing={2}>
              <Text className="stats-text" fontSize="sm" color={mutedColor}>
                {characterCount} characters
                {finalTime !== null && ` in ${finalTime} seconds`}
              </Text>
              
              {/* Stop Button - only show when streaming */}
              {isStreaming && (
                <Button
                  className="action-button stop-button"
                  leftIcon={<Text fontSize="sm">⏹</Text>}
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={handleStop}
                >
                  Stop
                </Button>
              )}
              
              {/* Edit Button */}
              <Button
                className="action-button"
                leftIcon={<Text fontSize="sm">{isEditing ? '👁' : '✏️'}</Text>}
                size="sm"
                variant="outline"
                onClick={handleEditToggle}
                title={isEditing ? 'Preview' : 'Edit'}
                isDisabled={!activeContent}
              >
                {isEditing ? 'Preview' : 'Edit'}
              </Button>
              
              {/* Save Button - only visible when editing */}
              {isEditing && (
                <Button
                  className="action-button"
                  leftIcon={<Text fontSize="sm">💾</Text>}
                  size="sm"
                  variant="solid"
                  colorScheme="blue"
                  onClick={() => {
                    onContentUpdate?.(editorContent, false);
                    console.log('💾 Content saved explicitly');
                    toast({
                      title: 'Content saved',
                      status: 'success',
                      duration: 2000,
                      isClosable: true,
                    });
                  }}
                  title="Save changes"
                >
                  Save
                </Button>
              )}
              
              {/* Clear Button */}
              <Button
                className="action-button"
                leftIcon={<Text fontSize="sm">🗑️</Text>}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={() => {
                  if (!config?.id) {
                    console.warn('No config ID available for ResponseViewer clear');
                    return;
                  }
                  
                  // Clear response content for current document only
                  threadService.updateThread(config.id, {
                    responseContent: '',
                    streamingContent: '',
                    error: null,
                    finalTime: null,
                    isStreaming: false
                  });
                  
                  // Reset local state
                  setContent('');
                  setEditorContent('');
                  setError(null);
                  setIsStreaming(false);
                  setFinalTime(null);
                  setStartTime(null);
                  setIsEditing(false);

                  // Reset UI states
                  setCopySuccess(false);
                  setExportSuccess(false);
                  setGitlabUploadSuccess(false);
                  
                  // Notify parent
                  onContentUpdate?.('', false);
                  
                  toast({
                    title: 'Response cleared',
                    description: `Response cleared for "${config.name || 'this document'}". Chat history and other documents remain unaffected.`,
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                  });
                }}
                title="Clear response content for this document only"
                isDisabled={!activeContent}
              >
                Clear
              </Button>
              
              {/* Fullscreen Button */}
              <Button
                className="action-button"
                leftIcon={isFullscreen ? <FaCompress /> : <FaExpand />}
                size="sm"
                variant="outline"
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? 'Exit' : 'Fullscreen'}
              </Button>
              
              {/* Copy Button with Format Dropdown */}
              <Box>
                <Menu isOpen={showCopyFormatMenu} onClose={() => setShowCopyFormatMenu(false)}>
                  <MenuButton
                    as={Button}
                    className="action-button"
                    leftIcon={copySuccess ? <CheckIcon /> : <CopyIcon />}
                    rightIcon={<ChevronDownIcon />}
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCopyFormatMenu(!showCopyFormatMenu)}
                    isDisabled={!activeContent}
                  >
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </MenuButton>
                  <MenuList minW="180px">
                    <MenuItem
                      onClick={() => {
                        setCopyFormat('markdown');
                        sessionStorage.setItem('copyFormat', 'markdown');
                        handleCopy();
                      }}
                    >
                      <HStack spacing={2} justify="space-between" w="full">
                        <Text>Markdown</Text>
                        {copyFormat === 'markdown' && <CheckIcon color="green.500" />}
                      </HStack>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setCopyFormat('rtf');
                        sessionStorage.setItem('copyFormat', 'rtf');
                        handleCopy();
                      }}
                    >
                      <HStack spacing={2} justify="space-between" w="full">
                        <Text>RTF (Word/Excel)</Text>
                        {copyFormat === 'rtf' && <CheckIcon color="green.500" />}
                      </HStack>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Box>
              
              {/* Download Dropdown */}
              <Box className="download-dropdown">
                <Menu isOpen={showDownloadDropdown} onClose={() => setShowDownloadDropdown(false)}>
                  <MenuButton
                    as={Button}
                    className="action-button"
                    rightIcon={<ChevronDownIcon className="dropdown-arrow" />}
                    leftIcon={exportSuccess ? <CheckIcon /> : <DownloadIcon />}
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                    isDisabled={!activeContent}
                  >
                    {exportSuccess ? 'Exported!' : 'Download'}
                  </MenuButton>
                  <MenuList className="dropdown-menu">
                    {/* Filename Input Section */}
                    <Box p={3} borderBottom="1px" borderColor={borderColor}>
                      <VStack spacing={2} align="stretch">
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight="semibold">Filename</Text>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => setShowFilenameInput(!showFilenameInput)}
                          >
                            {showFilenameInput ? 'Hide' : 'Customize'}
                          </Button>
                        </HStack>
                        {showFilenameInput ? (
                          <Input
                            size="sm"
                            placeholder={generateDefaultFilename('md')}
                            value={customFilename}
                            onChange={(e) => setCustomFilename(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                setShowFilenameInput(false);
                              }
                            }}
                          />
                        ) : (
                          <Text fontSize="xs" color={mutedColor}>
                            {customFilename || generateDefaultFilename('md')}
                          </Text>
                        )}
                      </VStack>
                    </Box>
                    
                    <MenuDivider />
                    
                    <MenuItem className="dropdown-item" onClick={() => handleDownload('md')}>
                      <HStack spacing={2}>
                        <Text>📄</Text>
                        <Text>Download as MD</Text>
                      </HStack>
                    </MenuItem>
                    <MenuItem className="dropdown-item" onClick={() => handleDownload('docx')}>
                      <HStack spacing={2}>
                        <Text>📝</Text>
                        <Text>Download as DOCX</Text>
                      </HStack>
                    </MenuItem>
                    <MenuItem className="dropdown-item" onClick={() => handleDownload('pdf')}>
                      <HStack spacing={2}>
                        <Text>📋</Text>
                        <Text>Download as PDF</Text>
                      </HStack>
                    </MenuItem>
                    
                    {gitlabAvailable && (
                      <>
                        <MenuDivider />
                        <MenuItem 
                          className="dropdown-item" 
                          onClick={onRepoModalOpen}
                          isDisabled={gitlabUploading}
                        >
                          <HStack spacing={2}>
                            <FaGitlab color="orange" />
                            <Text>{gitlabUploading ? 'Saving...' : 'Save to Repository'}</Text>
                            {gitlabUploadSuccess && <CheckIcon color="green.500" />}
                          </HStack>
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Menu>
              </Box>
            </HStack>
          </Box>
        </Flex>
      </Box>

      <Box className="viewer-content" 
           flex="1" 
           display="flex" 
           flexDirection="column" 
           overflow="hidden">
        <Box className="markdown-body"
             ref={contentScrollRef}
             p={isEditing ? 0 : 6}
             flex="1"
             overflowY={isEditing ? "hidden" : "auto"}
             bg={cardBg}
             minH="200px"
             h={isEditing ? "100%" : "auto"}
             onScroll={isEditing ? undefined : handleScroll}
             display="flex"
             flexDirection="column"
             position="relative"
             pointerEvents={isEditing ? "auto" : "auto"}
             zIndex={isEditing ? 10 : 1}>
          {renderContent}
          {isStreaming && (
            <HStack mt={4} spacing={2} py={2}>
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="sm" color={mutedColor}>
                Streaming response...
              </Text>
            </HStack>
          )}
        </Box>
      </Box>
      
      {/* Enhanced Save to Repository Modal */}
      <Modal isOpen={isRepoModalOpen} onClose={handleRepoModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="80vh" w="1200px" h="900px">
          <ModalHeader>Save to Repository</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* File Format Selection */}
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Choose the file format:
                </Text>
                <RadioGroup value={selectedFormat} onChange={(value) => setSelectedFormat(value as 'md' | 'docx' | 'pdf' | 'txt')}>
                  <Stack spacing={3}>
                    <Radio value="md">
                      <HStack spacing={2}>
                        <Text>📄</Text>
                        <Text>Markdown (.md)</Text>
                      </HStack>
                    </Radio>
                    <Radio value="txt">
                      <HStack spacing={2}>
                        <Text>📝</Text>
                        <Text>Plain Text (.txt)</Text>
                      </HStack>
                    </Radio>
                    <Radio value="docx">
                      <HStack spacing={2}>
                        <Text>📄</Text>
                        <Text>Word Document (.docx)</Text>
                      </HStack>
                    </Radio>
                    <Radio value="pdf">
                      <HStack spacing={2}>
                        <Text>📋</Text>
                        <Text>PDF Document (.pdf)</Text>
                      </HStack>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>

              <Divider />

              {/* Filename Input */}
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Choose filename:
                </Text>
                <VStack spacing={3} align="stretch">
                  <Input
                    placeholder={generateDefaultFilename(selectedFormat)}
                    value={customFilename}
                    onChange={(e) => setCustomFilename(e.target.value)}
                    size="sm"
                  />
                  <Text fontSize="xs" color={mutedColor}>
                    Leave empty to use default filename: {generateDefaultFilename(selectedFormat)}
                  </Text>
                </VStack>
              </Box>

              <Divider />

              {/* Path Selection */}
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Choose save location:
                </Text>
                
                {/* Suggested Path Display */}
                <Box p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md" mb={3}>
                  <Text fontSize="sm" color={mutedColor} mb={1}>
                    Suggested output path:
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">
                    📁 {suggestedPath || 'Calculating...'}
                  </Text>
                </Box>

                {/* Path Choice */}
                <VStack spacing={3} align="stretch">
                  <Text fontSize="sm" color={mutedColor}>
                    Do you want to use this path or choose another folder?
                  </Text>
                  
                  <HStack spacing={3}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="green"
                      onClick={() => setSelectedFolder(suggestedPath)}
                      isDisabled={!suggestedPath}
                    >
                      Use Suggested Path
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => {
                        setShowFolderBrowser(true);
                        loadAvailableFolders();
                      }}
                      leftIcon={<FaGitlab />}
                    >
                      Browse Folders
                    </Button>
                  </HStack>
                </VStack>

                {/* Selected Path Display */}
                {selectedFolder && (
                  <Box p={3} bg={useColorModeValue('blue.50', 'blue.900')} borderRadius="md" mt={3}>
                    <Text fontSize="sm" color={mutedColor} mb={1}>
                      Selected save location:
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" fontFamily="mono" color="blue.600">
                      📁 {selectedFolder}
                    </Text>
                  </Box>
                )}
              </Box>

              {/* Folder Browser */}
              {showFolderBrowser && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={3}>
                    Select target folder:
                  </Text>
                  
                  {loadingFolders ? (
                    <Center py={4}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" ml={2}>Loading folders...</Text>
                    </Center>
                  ) : (
                    <Box maxH="200px" overflowY="auto" border="1px" borderColor={borderColor} borderRadius="md">
                      <VStack spacing={0} align="stretch">
                        {availableFolders.map((folder, index) => (
                          <Box
                            key={index}
                            position="relative"
                            onMouseEnter={() => setHoveredFolder(folder)}
                            onMouseLeave={() => setHoveredFolder(null)}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              justifyContent="flex-start"
                              onClick={() => {
                                setSelectedFolder(folder);
                                setShowFolderBrowser(false);
                              }}
                              bg={selectedFolder === folder ? useColorModeValue('blue.50', 'blue.900') : 'transparent'}
                              _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
                              borderRadius={0}
                              borderBottom="1px"
                              borderColor={borderColor}
                              w="full"
                            >
                              <HStack spacing={2} justify="space-between" w="full">
                                <HStack spacing={2}>
                                  <FaFolder color="orange" />
                                  <Text fontSize="sm" fontFamily="mono">
                                    {folder || '(Root)'}
                                  </Text>
                                </HStack>
                                
                                {/* Add Sub-folder Button (shown on hover) */}
                                {hoveredFolder === folder && (
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="green"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowCreateFolder(true);
                                      setNewFolderName('');
                                    }}
                                    _hover={{ bg: 'green.100' }}
                                    px={2}
                                    py={1}
                                    fontSize="xs"
                                  >
                                    + Add a Sub-Folder
                                  </Button>
                                )}
                              </HStack>
                            </Button>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                  
                  <HStack spacing={2} mt={3}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowFolderBrowser(false)}
                    >
                      Cancel Browse
                    </Button>
                  </HStack>
                  
                  {/* Create New Folder Form */}
                  {showCreateFolder && (
                    <Box mt={4} p={4} border="1px" borderColor={borderColor} borderRadius="md" bg={useColorModeValue('gray.50', 'gray.700')}>
                      <Text fontSize="sm" fontWeight="semibold" mb={3}>
                        Create Sub-folder in: {hoveredFolder || '(Root)'}
                      </Text>
                      
                      <VStack spacing={3} align="stretch">
                        <Input
                          placeholder="Enter sub-folder name (e.g., My Documents)"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          size="sm"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newFolderName.trim()) {
                              createNewFolder(hoveredFolder || '');
                            }
                          }}
                        />
                        
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme="green"
                            onClick={() => createNewFolder(hoveredFolder || '')}
                            isLoading={creatingFolder}
                            isDisabled={!newFolderName.trim()}
                          >
                            Create Sub-folder
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setShowCreateFolder(false);
                              setNewFolderName('');
                            }}
                            isDisabled={creatingFolder}
                          >
                            Cancel
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>
                  )}
                </Box>
              )}
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleRepoModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={() => {
                handleGitlabUpload(selectedFormat);
                handleRepoModalClose();
              }}
              isLoading={gitlabUploading}
              leftIcon={<FaGitlab />}
              isDisabled={!selectedFolder}
            >
              Save to Repository
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});